import { useRef, useEffect } from "react";
import { cn } from "../../utils/classNames";
import XButton from "../common/XButton";
import ChevronIcon from "../../assets/icons/ChevronIcon.svg?react";

const wrapperClasses = "relative inline-flex flex-col items-end";

const groupBaseClasses = "inline-flex overflow-hidden shadow-sm";
const groupClosedClasses = "rounded-md";
const groupOpenClasses = "rounded-t-md";

const primaryBtnOverrides = "rounded-none! border-0! w-auto! justify-center! px-5!";
const chevronBtnOverrides = "rounded-none! border-0! w-auto! min-w-0! px-3!";

const dividerBaseClasses = "w-px self-stretch shrink-0";

// A gray ring and divider only read as seams on a light button; on a filled one they need to be the
// button's own colour, knocked back.
const VARIANT_FRAME_CLASSES = {
  secondary: { frame: "ring-1 ring-border", divider: "bg-border" },
  primary: { frame: "", divider: "bg-white/30" },
};

// Inherits the button's own text colour so it stays legible on a filled variant.
const chevronIconClasses = "w-4 h-4 transition-transform duration-200";
const chevronIconOpenClasses = "rotate-180";

const dropdownClasses =
  "absolute top-full right-0 w-full min-w-full " +
  "bg-action-surface border border-t-0 border-border rounded-b-md " +
  "overflow-hidden shadow-dropdown origin-top transition-all duration-150 z-[1000]";
const dropdownOpenClasses = "opacity-100 scale-y-100 pointer-events-auto";
const dropdownClosedClasses = "opacity-0 scale-y-0 pointer-events-none";

const menuItemBaseClasses =
  "flex items-center gap-2 w-full text-left px-4 py-2.5 bg-transparent border-0 " +
  "text-sm font-medium text-action cursor-pointer " +
  "hover:bg-primary-surface transition-colors duration-100 " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary";
const menuItemSeparatorClasses = "border-t border-border-light";
const menuItemDisabledClasses = "opacity-50 cursor-not-allowed pointer-events-none";

const SplitDropdownButton = ({
  isOpen,
  onToggle,
  primaryLabel,
  onPrimaryAction,
  menuItems = [],
  variant = "secondary",
  disabled = false,
  loading = false,
  loadingText,
  className,
  menuClassName,
}) => {
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        onToggle();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onToggle]);

  // with nothing to choose from the chevron and menu would be dead weight, so it is just a button
  if (menuItems.length === 0) {
    return (
      <XButton
        variant={variant}
        disabled={disabled}
        loading={loading}
        loadingText={loadingText}
        onClick={onPrimaryAction}
        className={className}
      >
        {primaryLabel}
      </XButton>
    );
  }

  const frameClasses = VARIANT_FRAME_CLASSES[variant] ?? VARIANT_FRAME_CLASSES.secondary;

  return (
    <div className={cn(wrapperClasses, className)} ref={wrapperRef}>
      <div
        className={cn(
          groupBaseClasses,
          frameClasses.frame,
          isOpen ? groupOpenClasses : groupClosedClasses
        )}
      >
        <XButton
          variant={variant}
          disabled={disabled}
          loading={loading}
          loadingText={loadingText}
          onClick={onPrimaryAction}
          className={primaryBtnOverrides}
        >
          {primaryLabel}
        </XButton>

        <div className={cn(dividerBaseClasses, frameClasses.divider)} />

        <XButton
          variant={variant}
          disabled={disabled || loading}
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label="More options"
          className={chevronBtnOverrides}
        >
          <ChevronIcon className={cn(chevronIconClasses, isOpen && chevronIconOpenClasses)} />
        </XButton>
      </div>

      <div
        className={cn(
          dropdownClasses,
          menuClassName,
          isOpen ? dropdownOpenClasses : dropdownClosedClasses
        )}
      >
        {menuItems.map(({ label, icon, action, disabled: itemDisabled }, i) => (
          <button
            key={label}
            type="button"
            onClick={() => { action?.(); onToggle(); }}
            className={cn(menuItemBaseClasses, i !== 0 && menuItemSeparatorClasses, itemDisabled && menuItemDisabledClasses)}
            disabled={itemDisabled}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};


export default SplitDropdownButton;
